// ──────────────────────────────────────────────────────
// Kiosk Identity Resolver — Single Source of Truth
//
// Priority:
//   1. Runtime-provisioned credentials from localStorage
//   2. Env vars (NEXT_PUBLIC_KIOSK_ID/KEY) — self-healing fallback
//   3. No credentials → device is unprovisioned → show gate
//
// This module is the ONLY place that determines which
// kioskId and kioskKey the frontend should use.
// ──────────────────────────────────────────────────────

// ── localStorage keys ────────────────────────────────
const LS_KIOSK_ID = 'qcareKioskId';
const LS_KIOSK_KEY = 'qcareKioskKey';
const LS_FINGERPRINT = 'qcareDeviceFingerprint';

// ── Env values ──────────────────────────────────────
const ENV_TENANT_ID = process.env.NEXT_PUBLIC_TENANT_ID || 't1';
const ENV_STORE_ID = process.env.NEXT_PUBLIC_STORE_ID || 's1';
const ENV_KIOSK_ID = process.env.NEXT_PUBLIC_KIOSK_ID || '';
const ENV_KIOSK_KEY = process.env.NEXT_PUBLIC_KIOSK_KEY || '';

function isValidCredential(value: string | null | undefined): value is string {
    return !!value && value.length > 0;
}

export type IdentitySource = 'localStorage' | 'none';

export interface KioskIdentity {
    kioskId: string;
    kioskKey: string;
    tenantId: string;
    storeId: string;
    source: IdentitySource;
    provisioned: boolean;
}

/**
 * Resolve the current kiosk identity.
 * Safe to call from client components (checks typeof window).
 *
 * Priority: localStorage > env vars > unprovisioned
 */
export function resolveIdentity(): KioskIdentity {
    // Runtime-provisioned credentials from localStorage
    if (typeof window !== 'undefined') {
        const lsKioskId = localStorage.getItem(LS_KIOSK_ID);
        const lsKioskKey = localStorage.getItem(LS_KIOSK_KEY);
        if (isValidCredential(lsKioskId) && isValidCredential(lsKioskKey)) {
            return {
                kioskId: lsKioskId,
                kioskKey: lsKioskKey,
                tenantId: ENV_TENANT_ID,
                storeId: ENV_STORE_ID,
                source: 'localStorage',
                provisioned: true,
            };
        }
    }

    // Fallback: env vars (self-healing after cache clear)
    if (isValidCredential(ENV_KIOSK_ID) && isValidCredential(ENV_KIOSK_KEY)) {
        // Persist to localStorage so future calls use the fast path
        if (typeof window !== 'undefined') {
            localStorage.setItem(LS_KIOSK_ID, ENV_KIOSK_ID);
            localStorage.setItem(LS_KIOSK_KEY, ENV_KIOSK_KEY);
        }
        return {
            kioskId: ENV_KIOSK_ID,
            kioskKey: ENV_KIOSK_KEY,
            tenantId: ENV_TENANT_ID,
            storeId: ENV_STORE_ID,
            source: 'localStorage',
            provisioned: true,
        };
    }

    // No valid credentials → unprovisioned → approval gate
    return {
        kioskId: '',
        kioskKey: '',
        tenantId: ENV_TENANT_ID,
        storeId: ENV_STORE_ID,
        source: 'none',
        provisioned: false,
    };
}

/**
 * Clear runtime-provisioned credentials from localStorage.
 * Called when backend rejects credentials (401).
 * Does NOT affect env credentials (they are not writable).
 */
export function clearRuntimeCredentials(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(LS_KIOSK_ID);
    localStorage.removeItem(LS_KIOSK_KEY);
}

/**
 * Build standard headers for kiosk-authenticated requests.
 * Includes x-kiosk-runtime marker for runtime-provisioned devices
 * so the proxy route knows not to override with env credentials.
 */
export function buildKioskHeaders(identity?: KioskIdentity): Record<string, string> {
    const id = identity ?? resolveIdentity();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Kiosk-Key': id.kioskKey,
    };
    if (id.source === 'localStorage') {
        headers['x-kiosk-runtime'] = '1';
    }
    return headers;
}

/**
 * Store runtime-provisioned credentials in localStorage.
 * Called after successful claim in Phase 3.
 */
export function storeRuntimeCredentials(kioskId: string, kioskKey: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(LS_KIOSK_ID, kioskId);
    localStorage.setItem(LS_KIOSK_KEY, kioskKey);
}

/**
 * Get or create the device fingerprint (stable per browser).
 */
export function getFingerprint(): string {
    if (typeof window === 'undefined') return 'ssr';
    let fp = localStorage.getItem(LS_FINGERPRINT);
    if (!fp) {
        fp = `fp-${crypto.randomUUID()}`;
        localStorage.setItem(LS_FINGERPRINT, fp);
    }
    return fp;
}
